import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FiltersModal from 'src/components/common/modal/FiltersModal';
import IconSvg from 'src/components/common/ui/IconSvg';
import LoadingSpinner from 'src/components/common/ui/loader/LoadingSpinner';
import AudienceBarChart from 'src/components/pages/dashboard/AudienceBarChart';
import AudienceStats from 'src/components/pages/dashboard/AudienceStats';
import DashboardStats from 'src/components/pages/dashboard/DashboardStats';
import TopCreatives from 'src/components/pages/dashboard/TopCreatives';
import { AuthContext } from 'src/contexts';
import { useHttp } from 'src/hooks';
import { GenderStats, PerformanceData, TopAudience, TopCreative } from 'src/types/performance';

const CampaignPerformance = () => {
  const navigate = useNavigate();
  const { sendRequest, isLoading } = useHttp();
  const { accessToken, profile } = useContext(AuthContext);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false); // State to control modal visibility
  const { campaignId } = useParams<{ campaignId: string }>();

  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [genderStats, setGenderStats] = useState<GenderStats | null>(null);
  const [topAudience, setTopAudience] = useState<TopAudience | null>(null);
  const [topCreatives, setTopCreatives] = useState<TopCreative[]>([]);
  const [seeAllCreatives, setSeeAllCreatives] = useState(false);

  const getCampaignPerformance = async (campaignId: string) => {
    if (!campaignId) return;

    sendRequest(
      {
        url: `campaign/performance/${campaignId}`,
        method: 'GET',
        accessToken,
      },
      (data: any) => {
        if (data) {
          setPerformanceData(data.performanceData);
          setGenderStats(data.genderStats);
          setTopAudience(data.topAudience);
          setTopCreatives(data.topCreatives);
        }
      },
    );
  };

  useEffect(() => {
    if (accessToken && campaignId) {
      getCampaignPerformance(campaignId);
    }
  }, [accessToken]);

  // You can now use campaignId in your component
  console.log('Current Campaign ID:', campaignId);
  console.log('his is performance data:', performanceData);
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center pb-4">
        <div>
          <div className="flex items-center gap-2">
            {' '}
            {/* Added gap between icon and text */}
            <IconSvg
              icon="leftArrow"
              className="cursor-pointer" // Added cursor pointer
              onClick={() => navigate(-1)} // Added back navigation on click
            />
            <p className="lg:text-[28px] text-[20px] font-semibold text-black font-inter">
              Campaign Performance
            </p>
          </div>
          <p className="lg:block hidden text-base font-inter font-normal text-[#4A4D4F]">
            Here is how your campaign is performing
          </p>
        </div>
        {/* Conditionally render the button */}
        {profile?.facebook !== null && (
          <div className="flex items-center space-x-4">
            {/* Filters Button */}
            <button
              type="button"
              onClick={() => setIsFiltersModalOpen(true)} // Open the modal on click
              className="bg-[#00000014] text-black px-4 py-2 rounded-[10px] text-base font-medium flex items-center"
            >
              <span className="mr-2">
                <IconSvg icon="filter" />
              </span>{' '}
              Filters
            </button>

            {/* Create New Campaign Button */}
            <button
              type="button"
              //   onClick={() => navigate('/dashboard/create-campaign')}
              className="bg-[#6277FF] text-white px-4 py-2 rounded-[10px] text-base font-medium flex items-center"
            >
              <span className="mr-2">
                <IconSvg icon="upload" />
              </span>{' '}
              Upload creatives
            </button>
          </div>
        )}
      </div>
      <p className="lg:hidden block text-sm font-inter font-normal text-[#4A4D4F]">
        Your Performance Snapshot: Insights to Keep You on Track and Optimizing.
      </p>
      <DashboardStats performanceData={performanceData} />
      <AudienceStats
        genderStats={genderStats}
        ageRangeStats={topAudience?.ageRanges} // Assuming your API returns age ranges in this format
      />{' '}
      <AudienceBarChart topAudience={topAudience} />
      <TopCreatives topCreatives={topCreatives} />{' '}
      {isFiltersModalOpen && <FiltersModal onClose={() => setIsFiltersModalOpen(false)} />}
    </div>
  );
};

export default CampaignPerformance;
